import { useCallback, useEffect, useState } from 'react';
import { apiService } from '../../services/api.service';

export interface BanksReportFilters {
  bank: string; // 'all' | bank documentId
  type: string; // 'all' | 'debit' | 'credit'
  dateFrom: string;
  dateTo: string;
  search: string;
}

export interface BanksReportSummary {
  totalBanks: number;
  totalBalance: number;
  totalCredit: number;
  totalDebit: number;
}

/**
 * Fetches the bank statement report (GET /api/banks/report).
 * All filters are applied server-side; one result block per bank.
 */
export function useBanksReport(filters: BanksReportFilters) {
  const [summary, setSummary] = useState<BanksReportSummary | null>(null);
  const [results, setResults] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchReport = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (filters.bank && filters.bank !== 'all') params.set('bank', filters.bank);
      if (filters.type && filters.type !== 'all') params.set('type', filters.type);
      if (filters.dateFrom) params.set('dateFrom', filters.dateFrom);
      if (filters.dateTo) params.set('dateTo', filters.dateTo);
      if (filters.search) params.set('search', filters.search);
      const qs = params.toString();
      const res: any = await apiService.get(`/banks/report${qs ? `?${qs}` : ''}`);
      setSummary(res?.summary || null);
      setResults(Array.isArray(res?.results) ? res.results : []);
    } catch (e: any) {
      setError(e?.error?.message || e?.message || 'Failed to load banks report');
    } finally {
      setIsLoading(false);
    }
  }, [filters.bank, filters.type, filters.dateFrom, filters.dateTo, filters.search]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  return { summary, results, isLoading, error, refetch: fetchReport };
}

/** Debounce a string value (used so search doesn't refetch on every keystroke) */
export function useDebouncedValue(value: string, delayMs = 400): string {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs]);
  return debounced;
}
